import { t, toEnglish } from '../../i18n/i18n';
import './StatusChip.scss';

/**
 * The five chip states the Style Guide ships (`.accounts_status` / `.breach_chip-*`).
 * Anything not listed falls back to neutral rather than inventing a colour —
 * the guide bans adding more signal colours than these.
 */
const TONE_BY_LABEL = {
  active: 'success',
  clear: 'success',
  approved: 'success',
  // Payment History reads the transaction API verbatim, which says
  // "Succeeded" / "Failed" where the design draws Approved / Rejected.
  succeeded: 'success',
  passed: 'success',
  // The accounts endpoint reports an account's `progress` as Success / Failed /
  // In Progress / Time Expired. The other three already resolve; this one was
  // falling through to neutral and reading like nothing had happened.
  success: 'success',
  'tp hit': 'success',
  partial: 'success',
  'on track': 'success',
  verified: 'success',

  warning: 'warning',
  'in progress': 'warning',
  pending: 'warning',
  'not started': 'warning',

  evaluation: 'info',
  // The same journey as Evaluation, one stage on — the dashboard lists both as
  // an account `status`, so they should not read as different kinds of thing.
  verification: 'info',
  'verification 2': 'info',
  funded: 'info',
  // Funded's new name (Gerry's review). Green, not the blue every other
  // account type wears: a Qualified account is the one that made it, and the
  // Account Type column should say so at a glance (review: "should have a
  // color" — it was falling through to neutral grey).
  qualified: 'success',
  // Matches the Affiliate request screens' own success/info/danger convention
  // (StatusFilterChips) and the Payment History design (1:12933) — not warning.
  processing: 'info',

  // The API pads the number ("Phase 01"); `phaseLabel` writes it "Phase 1".
  'phase 01': 'stage',
  'phase 02': 'stage',
  'phase 03': 'stage',
  'phase 1': 'stage',
  'phase 2': 'stage',
  'phase 3': 'stage',

  'hard breach': 'danger',
  'at risk': 'danger',
  rejected: 'danger',
  failed: 'danger',
  blocked: 'danger',
  'sl hit': 'danger',
  breached: 'danger',
};

export function toneFor(label) {
  return TONE_BY_LABEL[String(label).trim().toLowerCase()] ?? 'neutral';
}

export default function StatusChip({ children, tone, size }) {
  // Callers pass the English value or one already translated; either way the
  // tone is read off the English, and the label is translated exactly once.
  const english = toEnglish(children);
  const resolved = tone ?? toneFor(english);
  // Danger chips are drawn one step larger in the design — 32px/13px against
  // 28.8px/11.2px for every other tone (1:8123, 1:11890).
  const resolvedSize = size ?? (resolved === 'danger' ? 'md' : 'sm');
  return (
    <span className={`status-chip status-chip--${resolved} status-chip--${resolvedSize}`}>{t(english)}</span>
  );
}
